import React, { useEffect, useState } from "react";
import {
  getDepartmentOptions,
  getSubDepartments,
  getTeamDetails,
  getTeamsByDepartment,
  TeamMember,
} from "./api/organization.api";
import UserAvatar from "../../ui/icons/UserAvatar";
import { useNavigate } from "react-router-dom";
import { useTeam } from "../../../features/TeamcompositeResults/contexts/TeamContext";
import { DropdownChevronPair } from "../DropdownChevronPair";

interface Option {
  id: number;
  name: string;
}

export type DeptTeamSelectorMode = "org" | "dept_lead" | "user";

interface MobileDeptTeamSelectorProps {
  organizationId: number;
  roles: any[];
  teams: Option[];
  teamsLeading: Option[];
  allowedDepartments?: Option[];
  onTeamSelect?: (team: Option) => void;
  onClose?: () => void;
}

const ORG_LEVEL_ROLES = ["ORG_LEAD"];
const DEPT_LEVEL_ROLES = ["DEPT_LEAD"];

const MobileDeptTeamSelector: React.FC<MobileDeptTeamSelectorProps> = ({
  organizationId,
  roles,
  teams: userTeams,
  teamsLeading,
  allowedDepartments = [],
  onTeamSelect,
  onClose,
}) => {
  const roleCodes = roles.map((r: any) => r.code);
  const navigate = useNavigate();
  const isOrgLevel = roleCodes.some((c: string) => ORG_LEVEL_ROLES.includes(c));
  const isDeptLevel = roleCodes.some((c: string) =>
    DEPT_LEVEL_ROLES.includes(c),
  );
  const isTeamLead = roleCodes.includes("TEAM_LEAD");
  const isPlainUser = !isOrgLevel && !isDeptLevel && !isTeamLead;

  const mode: DeptTeamSelectorMode = isOrgLevel
    ? "org"
    : isDeptLevel
      ? "dept_lead"
      : "user";

  const [departments, setDepartments] = useState<Option[]>([]);
  const [teams, setTeams] = useState<Option[]>([]);
  const [isLeadingExpanded, setIsLeadingExpanded] = useState(false);

  const [subDeptMap, setSubDeptMap] = useState<Record<number, Option[]>>({});
  const [expandedSubDept, setExpandedSubDept] = useState<number | null>(null);
  const [subDeptTeamsMap, setSubDeptTeamsMap] = useState<Record<number, Option[]>>({});

  const [expandedDept, setExpandedDept] = useState<number | null>(null);
  const [expandedTeam, setExpandedTeam] = useState<number | null>(null);
  const [deptTeamsMap, setDeptTeamsMap] = useState<Record<number, Option[]>>(
    {},
  );
  const [expandedLeadingTeam, setExpandedLeadingTeam] = useState<number | null>(null);
  const [teamMembersMap, setTeamMembersMap] = useState<
    Record<number, TeamMember[]>
  >({});

  const [deptLoading, setDeptLoading] = useState(false);
  const [teamLoading, setTeamLoading] = useState(false);
  const { setSelectedDepartment, setSelectedTeam } = useTeam();

  const handleNavigateToUsers = (teamId: number, members: TeamMember[]) => {
    if (!members || members.length === 0) return;

    navigate(`/user-management?team_id=${teamId}`);
    onClose?.();
  };

  const handleExpandLeadingTeam = async (teamId: number) => {
    setExpandedLeadingTeam((prev) => (prev === teamId ? null : teamId));

    if (teamMembersMap[teamId]) return;

    try {
      const res = await getTeamDetails(teamId);
      if (res?.success && res.data) {
        setTeamMembersMap((prev) => ({
          ...prev,
          [teamId]: res.data.members ?? [],
        }));
      }
    } catch (err) {
      console.warn(err);
    }
  };

  // ── ORG mode: fetch all departments ──
  useEffect(() => {
    if (mode !== "org" || !organizationId) return;
    const fetchDepts = async () => {
      setDeptLoading(true);
      try {
        const res = await getDepartmentOptions({
          organization_id: organizationId,
          limit: 100,
        });
        if (res?.success && res.data?.rows?.length) {
          const mapped = res.data.rows.map((item: any) => ({
            id: Number(item.value),
            name: item.label,
          }));
          setDepartments(mapped);
        }
      } catch (err) {
        console.warn(err);
      } finally {
        setDeptLoading(false);
      }
    };
    fetchDepts();
  }, [mode, organizationId]);

  // ── DEPT_LEAD mode: use allowedDepartments ──
  useEffect(() => {
    if (mode !== "dept_lead") return;
    if (allowedDepartments.length > 0) setDepartments(allowedDepartments);
    else {
      // fallback: fetch by org
      const fetchDepts = async () => {
        setDeptLoading(true);
        try {
          const res = await getDepartmentOptions({
            organization_id: organizationId,
            limit: 100,
          });
          if (res?.success && res.data?.rows?.length) {
            setDepartments(
              res.data.rows.map((item: any) => ({
                id: Number(item.value),
                name: item.label,
              })),
            );
          }
        } catch (err) {
          console.warn(err);
        } finally {
          setDeptLoading(false);
        }
      };
      fetchDepts();
    }
  }, [mode, allowedDepartments, organizationId]);

  // ── USER mode: use userTeams / teamsLeading ──
  useEffect(() => {
    if (mode !== "user") return;
    const source = isTeamLead ? teamsLeading : userTeams;
    setTeams(
      source.filter((t, i, s) => s.findIndex((x) => x.id === t.id) === i),
    );
  }, [mode, userTeams, teamsLeading, isTeamLead]);

  const handleExpandDept = async (deptId: number) => {
    setExpandedDept((prev) => (prev === deptId ? null : deptId));
    setExpandedSubDept(null); // 🔥 reset sub dept

    const dept = departments.find((d) => d.id === deptId);
    if (dept) {
      setSelectedDepartment(dept);
    }

    // 🔥 Fetch MAIN dept teams (existing)
    if (!deptTeamsMap[deptId]) {
      setTeamLoading(true);
      try {
        const res = await getTeamsByDepartment(deptId);
        if (res?.success && res.data?.rows?.length) {
          setDeptTeamsMap((prev) => ({
            ...prev,
            [deptId]: res.data.rows.map((item: any) => ({
              id: Number(item.value),
              name: item.label,
            })),
          }));
        }
      } catch (err) {
        console.warn(err);
      } finally {
        setTeamLoading(false);
      }
    }

    // 🔥 NEW: Fetch sub-departments
    if (!subDeptMap[deptId]) {
      try {
        const res = await getSubDepartments({
          organization_id: organizationId,
          department_id: deptId,
        });

        if (res?.success && res.data?.rows?.length) {
          const mapped = res.data.rows
            .filter((item) => item.parent?.id === deptId)
            .map((item) => ({
              id: item.id,
              name: item.name,
            }));

          setSubDeptMap((prev) => ({
            ...prev,
            [deptId]: mapped,
          }));
        }
      } catch (err) {
        console.warn("Failed to fetch sub departments", err);
      }
    }
  };

  const handleExpandSubDept = async (subDept: Option) => {
    setExpandedSubDept((prev) => (prev === subDept.id ? null : subDept.id));
    setSelectedDepartment(subDept); // 🔥 treat as active dept

    if (subDeptTeamsMap[subDept.id]) return;

    setTeamLoading(true);
    try {
      const res = await getTeamsByDepartment(subDept.id);

      if (res?.success && res.data?.rows?.length) {
        setSubDeptTeamsMap((prev) => ({
          ...prev,
          [subDept.id]: res.data.rows.map((item: any) => ({
            id: Number(item.value),
            name: item.label,
          })),
        }));
      }
    } catch (err) {
      console.warn(err);
    } finally {
      setTeamLoading(false);
    }
  };

  // ── Expand team → fetch its members ──
  const handleExpandTeam = async (teamId: number) => {
    setExpandedTeam((prev) => (prev === teamId ? null : teamId));
    if (teamMembersMap[teamId]) return;
    try {
      const res = await getTeamDetails(teamId);
      if (res?.success && res.data) {
        setTeamMembersMap((prev) => ({
          ...prev,
          [teamId]: res.data.members ?? [],
        }));
      }
    } catch (err) {
      console.warn(err);
    }
  };

  // ── Plain USER — nothing to show ──
  if (isPlainUser) return null;

  // ── Loading ──
  if (deptLoading) {
    return (
      <div className="px-4 py-3 text-xs text-[#9ba5b4] font-primary animate-pulse">
        Loading...
      </div>
    );
  }

  // ── USER / TEAM_LEAD mode — show teams as accordion ──
  if (mode === "user") {
    if (teams.length === 0) return null;

    return (
      <div>
        {teams.map((team) => {
          const isExpanded = expandedTeam === team.id;
          const members = teamMembersMap[team.id] ?? [];
          return (
            <div
              key={team.id}
              className="border-b border-[#f0f3fa] last:border-0"
            >
              <button
                onClick={() => {
                  handleExpandTeam(team.id);
                  onTeamSelect?.(team);
                  setSelectedTeam(team);
                }}
                className="w-full flex items-center justify-between px-4 py-3 bg-transparent border-0 cursor-pointer hover:bg-[#f8f9ff] transition-colors"
              >
                <div className="flex items-center gap-2">
                  <svg
                    width="15"
                    height="15"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#7b9ded"
                    strokeWidth="2"
                  >
                    <rect x="3" y="3" width="7" height="7" rx="1" />
                    <rect x="14" y="3" width="7" height="7" rx="1" />
                    <rect x="3" y="14" width="7" height="7" rx="1" />
                    <rect x="14" y="14" width="7" height="7" rx="1" />
                  </svg>
                  <span className="text-[13px] font-semibold text-[#26295e] font-primary">
                    {team.name}
                  </span>
                </div>
                <DropdownChevronPair className="border-[#dfe3ec] bg-[#f4f6fa] text-[#9ba5b4]" />
              </button>

              {isExpanded && (
                <div className="px-4 pb-3">
                  {members.length === 0 ? (
                    <p className="text-xs text-[#9ba5b4] font-primary">
                      No members
                    </p>
                  ) : (
                    <div>
                      <div
                        className={`flex items-center gap-1 mb-2 ${members.length > 0
                          ? "cursor-pointer"
                          : "opacity-60 cursor-not-allowed"
                          }`}
                        onClick={() => handleNavigateToUsers(team.id, members)}
                      >
                        {members.slice(0, 3).map((m, i) => (
                          <div
                            key={m.id}
                            className="relative group"
                            style={{ zIndex: members.length - i }}
                          >
                            <UserAvatar
                              src={m.image ?? ""}
                              className="w-7 h-7 border-2 border-white"
                            />
                            <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 bg-[#26295e] text-white text-[10px] font-primary px-1.5 py-0.5 rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50">
                              {m.first_name} {m.last_name}
                            </div>
                          </div>
                        ))}
                        {members.length > 3 && (
                          <div className="w-7 h-7 rounded-full bg-[#5d4cc6] flex items-center justify-center text-white text-[9px] font-bold border-2 border-white">
                            +{members.length - 3}
                          </div>
                        )}
                        <span className="text-[11px] text-[#9ba5b4] font-primary ml-1">
                          {members.length} member
                          {members.length !== 1 ? "s" : ""}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  }

  // ── ORG / DEPT_LEAD mode — show departments accordion with nested teams ──
  return (
    <div>
      {teamsLeading.length > 0 && (
        <div className="border-b border-[#e6e9f2]">

          <button
            onClick={() => setIsLeadingExpanded(prev => !prev)}
            className="w-full flex items-center justify-between px-4 py-3 hover:bg-[#f8f9ff]"
          >
            <div className="flex items-center gap-2">
              <svg
                width="15"
                height="15"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#7b9ded"
                strokeWidth="2"
              >
                <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
              </svg>

              <span className="text-[13px] font-semibold text-[#26295e] font-primary">
                Teams Leading
              </span>
            </div>
          </button>

          {isLeadingExpanded && (
            <div className="bg-[#f8f9ff] border-t border-[#f0f3fa]">

              {teamsLeading.map((team) => {
                const isExpanded = expandedLeadingTeam === team.id;
                const members = teamMembersMap[team.id] ?? [];

                return (
                  <div key={team.id} className="border-b border-[#eef0f8] last:border-0">
                    <button
                      onClick={() => {
                        handleExpandLeadingTeam(team.id);
                        onTeamSelect?.(team);
                        setSelectedTeam(team);
                      }}
                      className="w-full flex items-center justify-between pl-8 pr-4 py-2.5 hover:bg-[#eef1fb]"
                    >
                      <div className="flex items-center gap-2">
                        <svg
                          width="13"
                          height="13"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="#7b9ded"
                          strokeWidth="2"
                        >
                          <rect x="3" y="3" width="7" height="7" rx="1" />
                          <rect x="14" y="3" width="7" height="7" rx="1" />
                          <rect x="3" y="14" width="7" height="7" rx="1" />
                          <rect x="14" y="14" width="7" height="7" rx="1" />
                        </svg>

                        <span className="text-[12px] font-medium text-[#26295e] font-primary">
                          {team.name}
                        </span>
                      </div>
                      <DropdownChevronPair />
                    </button>

                    {isExpanded && (
                      <div className="pl-8 pr-4 pb-3">
                        {members.length === 0 ? (
                          <p className="text-xs text-[#9ba5b4] font-primary">
                            No members
                          </p>
                        ) : (
                          <div
                            className={`flex items-center gap-1 ${members.length > 0
                              ? "cursor-pointer"
                              : "opacity-60 cursor-not-allowed"
                              }`}
                            onClick={() =>
                              handleNavigateToUsers(team.id, members)
                            }
                          >
                            {members.slice(0, 3).map((m, i) => (
                              <div
                                key={m.id}
                                className="relative group"
                                style={{ zIndex: members.length - i }}
                              >
                                <UserAvatar
                                  src={m.image ?? ""}
                                  className="w-7 h-7 border-2 border-white"
                                />

                                {/* tooltip */}
                                <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 bg-[#26295e] text-white text-[10px] font-primary px-1.5 py-0.5 rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50">
                                  {m.first_name} {m.last_name}
                                </div>
                              </div>
                            ))}

                            {members.length > 3 && (
                              <div className="w-7 h-7 rounded-full bg-[#5d4cc6] flex items-center justify-center text-white text-[9px] font-bold border-2 border-white">
                                +{members.length - 3}
                              </div>
                            )}

                            <span className="text-[11px] text-[#9ba5b4] font-primary ml-1">
                              {members.length} member
                              {members.length !== 1 ? "s" : ""}
                            </span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}

            </div>
          )}
        </div>
      )}
      {departments.map((dept) => {
        const isExpanded = expandedDept === dept.id;
        const dTeams = deptTeamsMap[dept.id] ?? [];

        return (
          <div
            key={dept.id}
            className="border-b border-[#f0f3fa] last:border-0"
          >
            {/* Dept row */}
            <button
              onClick={() => handleExpandDept(dept.id)}
              className="w-full flex items-center justify-between px-4 py-3 bg-transparent border-0 cursor-pointer hover:bg-[#f8f9ff] transition-colors"
            >
              <div className="flex items-center gap-2">
                <svg
                  width="15"
                  height="15"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#7b9ded"
                  strokeWidth="2"
                >
                  <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
                </svg>
                <span className="text-[13px] font-semibold text-[#26295e] font-primary">
                  {dept.name}
                </span>
              </div>
              <DropdownChevronPair className="border-[#dfe3ec] bg-[#f4f6fa] text-[#9ba5b4]" />
            </button>

            {/* Dept teams */}
            {isExpanded && (
              <div className="bg-[#f8f9ff] border-t border-[#f0f3fa]">

                {/* ✅ MAIN DEPARTMENT TEAMS */}
                {teamLoading ? (
                  <p className="px-6 py-2 text-xs text-[#9ba5b4] font-primary animate-pulse">
                    Loading...
                  </p>
                ) : dTeams.length === 0 ? (
                  <p className="px-6 py-2 text-xs text-[#9ba5b4] font-primary">
                    No teams
                  </p>
                ) : (
                  dTeams.map((team) => {
                    const isTeamExpanded = expandedTeam === team.id;
                    const members = teamMembersMap[team.id] ?? [];

                    return (
                      <div key={team.id} className="border-b border-[#eef0f8] last:border-0">
                        <button
                          onClick={() => {
                            handleExpandTeam(team.id);
                            onTeamSelect?.(team);
                            setSelectedTeam(team);
                          }}
                          className="w-full flex items-center justify-between pl-8 pr-4 py-2.5 hover:bg-[#eef1fb]"
                        >
                          <span className="text-[12px] font-medium text-[#26295e] font-primary">
                            {team.name}
                          </span>
                          <DropdownChevronPair />
                        </button>

                        {isTeamExpanded && (
                          <div className="pl-8 pr-4 pb-3">
                            {members.length === 0 ? (
                              <p className="text-xs text-[#9ba5b4] font-primary">
                                No members
                              </p>
                            ) : (
                              <div
                                className="flex items-center gap-1 cursor-pointer"
                                onClick={() =>
                                  handleNavigateToUsers(team.id, members)
                                }
                              >
                                {members.slice(0, 3).map((m, i) => (
                                  <div
                                    key={m.id}
                                    className="relative group"
                                    style={{ zIndex: members.length - i }}
                                  >
                                    <UserAvatar
                                      src={m.image ?? ""}
                                      className="w-7 h-7 border-2 border-white"
                                    />
                                    <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 bg-[#26295e] text-white text-[10px] px-1.5 py-0.5 rounded opacity-0 group-hover:opacity-100">
                                      {m.first_name} {m.last_name}
                                    </div>
                                  </div>
                                ))}

                                {members.length > 3 && (
                                  <div className="w-7 h-7 rounded-full bg-[#5d4cc6] flex items-center justify-center text-white text-[9px] border-2 border-white">
                                    +{members.length - 3}
                                  </div>
                                )}

                                <span className="text-[11px] text-[#9ba5b4] ml-1">
                                  {members.length} member
                                  {members.length !== 1 ? "s" : ""}
                                </span>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })
                )}

                {/* ✅ SUB DEPARTMENTS (OPTIONAL) */}
                {subDeptMap[dept.id]?.length > 0 && (
                  <div className="border-t border-[#e6e9f2] mt-2">

                    <div className="px-4 py-2 text-[11px] text-[#9ba5b4] uppercase font-primary">
                      Sub Departments
                    </div>

                    {subDeptMap[dept.id].map((sub) => {
                      const isSubExpanded = expandedSubDept === sub.id;
                      const subTeams = subDeptTeamsMap[sub.id] ?? [];

                      return (
                        <div key={sub.id} className="border-b border-[#eef0f8]">

                          {/* Sub Dept Row */}
                          <button
                            onClick={() => handleExpandSubDept(sub)}
                            className="w-full flex items-center justify-between pl-10 pr-4 py-2 hover:bg-[#eef1fb]"
                          >
                            <span className="text-[12px] text-[#26295e] font-primary">
                              {sub.name}
                            </span>
                            <DropdownChevronPair />
                          </button>

                          {/* Sub Dept Teams */}
                          {isSubExpanded && (
                            <div>
                              {subTeams.length === 0 ? (
                                <p className="px-6 py-2 text-xs text-[#9ba5b4]">
                                  No teams
                                </p>
                              ) : (
                                subTeams.map((team) => {
                                  const members = teamMembersMap[team.id] ?? [];
                                  const isTeamExpanded = expandedTeam === team.id;

                                  return (
                                    <div key={team.id} className="pl-12">

                                      <button
                                        onClick={() => {
                                          handleExpandTeam(team.id);
                                          onTeamSelect?.(team);
                                          setSelectedTeam(team);
                                        }}
                                        className="w-full flex justify-between py-2 text-[12px] text-[#26295e]"
                                      >
                                        {team.name}
                                      </button>

                                      {isTeamExpanded && (
                                        <div className="pb-3">
                                          {members.length === 0 ? (
                                            <p className="text-xs text-[#9ba5b4]">
                                              No members
                                            </p>
                                          ) : (
                                            <div
                                              className="flex items-center gap-1 cursor-pointer"
                                              onClick={() =>
                                                handleNavigateToUsers(team.id, members)
                                              }
                                            >
                                              {members.slice(0, 3).map((m, i) => (
                                                <UserAvatar
                                                  key={m.id}
                                                  src={m.image ?? ""}
                                                  className="w-7 h-7 border-2 border-white"
                                                />
                                              ))}
                                            </div>
                                          )}
                                        </div>
                                      )}
                                    </div>
                                  );
                                })
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default MobileDeptTeamSelector;
